import {
  CalendarEventType,
  Calendar_CancellationStatusFilter,
} from '@tyro/api';
import { RHFAutocomplete } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { useMemo } from 'react';
import { type Control, useWatch } from 'react-hook-form';
import { useRoomLocation } from '../../../../api/add-event';
import type { FormState, RecurrenceFilter, RoomLocationOption } from './types';

type RoomLocationOptionsProps = {
  recurrenceFilter: RecurrenceFilter;
  control: Control<FormState>;
};

export const RoomLocationOptions = ({
  recurrenceFilter,
  control,
}: RoomLocationOptionsProps) => {
  const { t } = useTranslation(['common', 'calendar']);

  const eventType = useWatch({ control, name: 'type' });

  const { data: freeLocationData } = useRoomLocation({
    allRooms: true,
    resources: {},
    recurrence: recurrenceFilter,
    cancellationStatus: Calendar_CancellationStatusFilter.NotCancelled,
  });

  const roomLocationOptions = useMemo<RoomLocationOption[]>(
    () =>
      freeLocationData
        ? [
            ...freeLocationData.freeRooms.map((room) => ({
              ...room,
              type: t('calendar:inputLabels.freeLocation'),
            })),
            ...(eventType === CalendarEventType.RoomBooking
              ? []
              : freeLocationData.clashingRooms.map(({ room }) => ({
                  ...room,
                  type: t('calendar:inputLabels.clashingLocation'),
                }))),
          ]
        : [],
    [freeLocationData, eventType],
  );

  return (
    <RHFAutocomplete
      label={t('calendar:inputLabels.locations')}
      optionIdKey="roomId"
      optionTextKey="name"
      multiple
      groupBy={(option) => option.type ?? t('common:unknown')}
      controlProps={{ name: 'locations', control }}
      options={roomLocationOptions}
    />
  );
};
